import React from 'react';

/**
 * Skeleton Loader Components
 * Provides loading placeholders for various UI elements
 */

/**
 * Base Skeleton Component
 */
export const Skeleton = ({ className = '', width, height, ...props }) => {
  const style = {
    ...(width && { width }),
    ...(height && { height }),
    ...props.style,
  };

  return (
    <div 
      className={`skeleton ${className}`}
      style={style}
      role="status"
      aria-label="Loading"
    />
  );
};

/**
 * Text Skeleton - For text content
 */
export const TextSkeleton = ({ lines = 1, width = '100%' }) => (
  <div className="text-skeleton" style={{ width }}>
    {Array.from({ length: lines }).map((_, i) => (
      <Skeleton 
        key={i} 
        className={`text-skeleton-line text-skeleton-line-${i}`}
        style={{ 
          width: i === lines - 1 ? `${Math.random() * 40 + 60}%` : '100%' 
        }}
        height={16}
      />
    ))}
  </div>
);

/**
 * Avatar Skeleton - For user avatars
 */
export const AvatarSkeleton = ({ size = 40 }) => (
  <Skeleton 
    className="avatar-skeleton" 
    width={size} 
    height={size} 
    style={{ borderRadius: '50%' }}
  />
);

/**
 * Card Skeleton - For card components
 */
export const CardSkeleton = () => (
  <div className="card-skeleton">
    <div className="card-skeleton-header">
      <AvatarSkeleton size={48} />
      <div className="card-skeleton-info">
        <TextSkeleton lines={2} width="200px" />
      </div>
    </div>
    <div className="card-skeleton-body">
      <TextSkeleton lines={3} />
    </div>
    <div className="card-skeleton-footer">
      <Skeleton width={80} height={32} />
    </div>
  </div>
);

/**
 * List Skeleton - For list items
 */
export const ListSkeleton = ({ count = 5 }) => (
  <div className="list-skeleton">
    {Array.from({ length: count }).map((_, i) => (
      <div key={i} className="list-item-skeleton">
        <AvatarSkeleton size={40} />
        <div className="list-item-content">
          <TextSkeleton lines={1} width="60%" />
          <TextSkeleton lines={1} width="40%" />
        </div>
      </div>
    ))}
  </div>
);

/**
 * Table Skeleton - For table data
 */
export const TableSkeleton = ({ rows = 5, columns = 5 }) => (
  <div className="table-skeleton">
    <div className="table-header-skeleton">
      {Array.from({ length: columns }).map((_, i) => (
        <Skeleton key={i} width={100} height={32} />
      ))}
    </div>
    {Array.from({ length: rows }).map((_, rowIndex) => (
      <div key={rowIndex} className="table-row-skeleton">
        {Array.from({ length: columns }).map((_, colIndex) => (
          <Skeleton 
            key={colIndex} 
            width={Math.random() * 100 + 50} 
            height={40} 
          />
        ))}
      </div>
    ))}
  </div>
);

/**
 * Grid Skeleton - For grid layouts
 */
export const GridSkeleton = ({ items = 6 }) => (
  <div className="grid-skeleton">
    {Array.from({ length: items }).map((_, i) => (
      <div key={i} className="grid-item-skeleton">
        <Skeleton height={120} style={{ borderRadius: '8px' }} />
        <TextSkeleton lines={2} style={{ marginTop: '12px' }} />
      </div>
    ))}
  </div>
);

/**
 * Resident Profile Skeleton
 */
export const ResidentProfileSkeleton = () => (
  <div className="resident-profile-skeleton">
    <div className="profile-header-skeleton">
      <div className="profile-avatar-skeleton">
        <AvatarSkeleton size={80} />
      </div>
      <div className="profile-info-skeleton">
        <TextSkeleton lines={2} width="250px" />
        <TextSkeleton lines={1} width="150px" />
      </div>
    </div>
    <div className="profile-sections-skeleton">
      <CardSkeleton />
      <CardSkeleton />
      <CardSkeleton />
    </div>
  </div>
);

/**
 * Assessment Card Skeleton
 */
export const AssessmentCardSkeleton = () => (
  <div className="assessment-card-skeleton">
    <div className="assessment-card-header">
      <Skeleton width={40} height={40} style={{ borderRadius: '8px' }} />
      <div className="assessment-card-title">
        <TextSkeleton lines={1} width="150px" />
      </div>
    </div>
    <div className="assessment-card-body">
      <TextSkeleton lines={2} />
    </div>
  </div>
);

export default {
  Skeleton,
  TextSkeleton,
  AvatarSkeleton,
  CardSkeleton,
  ListSkeleton,
  TableSkeleton,
  GridSkeleton,
  ResidentProfileSkeleton,
  AssessmentCardSkeleton,
};
